import { useEffect, useState } from 'react';
import { Box, Stack, Text, TextField, RadioGroup, Radio, Spinner } from '@customerio/pluma-components/react';

interface User {
  user_id: number;
  email: string;
  deleted?: boolean;
}

interface UserFilterProps {
  value: string;
  onChange: (value: string) => void;
  allUsers: User[];
  isLoadingUsers: boolean;
  onLoadUsers?: () => void;
}

export function UserFilter({ value, onChange, allUsers, isLoadingUsers, onLoadUsers }: UserFilterProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Trigger lazy loading when filter opens
  useEffect(() => {
    if (onLoadUsers != null && allUsers.length === 0 && !isLoadingUsers) {
      onLoadUsers();
    }
  }, [onLoadUsers, allUsers.length, isLoadingUsers]);

  const systemUser = { user_id: 0, email: 'system@customer.io' };
  const allUsersWithSystem = [systemUser, ...allUsers];

  // Filter users based on search query (trim whitespace for better matching)
  const trimmedQuery = searchQuery.trim();
  const filteredUsers =
    trimmedQuery === ''
      ? allUsersWithSystem
      : allUsersWithSystem.filter((user) => user.email.toLowerCase().includes(trimmedQuery.toLowerCase()));

  // Show loading state while loading OR when users haven't been loaded yet
  const isLoading = isLoadingUsers || (allUsers.length === 0 && onLoadUsers != null);

  return (
    <Box p="300">
      <Stack gap="200">
        {/* Note: Using onInput instead of onChange is required for TextField to work correctly
            in DataTable filter popovers. onChange causes the input to become unresponsive. */}
        <TextField
          value={searchQuery}
          onInput={(e) => setSearchQuery(e.target.value)}
          placeholder="Search users..."
          label="Search users"
          disabled={isLoading}
        />
        <Box style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {isLoading ? (
            <Box display="flex" alignItems="center" gap="100" py="300">
              <Spinner />
              <Text size="s" color="subtle">
                Loading users...
              </Text>
            </Box>
          ) : filteredUsers.length > 0 ? (
            <RadioGroup value={value ?? ''} onChange={onChange} label="User Options">
              {filteredUsers.map((user) => {
                const isDeleted = 'deleted' in user && user.deleted === true;
                const labelText = isDeleted ? `${user.email} (Deleted)` : user.email;
                return <Radio key={user.user_id} value={user.user_id.toString()} label={labelText} />;
              })}
            </RadioGroup>
          ) : (
            <Text size="s" color="subtle">
              No users found
            </Text>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
